import {
  PromoCode,
  PromoCodeListResponse,
  ApiPromoCodeListResponse,
  ApiPromoCodeResponse,
  PromoCodeQueryParams,
  CreatePromoCodeDto,
  UpdatePromoCodeDto,
} from './promo-codes.dto';
import { logging, API_BASE_URL } from '@/config/env';

import { redirectToLogin } from '../../utils/sessionExpired';
// Custom logging function
const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class PromoCodesService {
  private baseUrl = API_BASE_URL;
  private logoutCallback: (() => void) | null = null;

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      let errorMessage = `Request failed with status ${response.status}`;

      // Try to parse error response to check for session expired
      try {
        const errorData = JSON.parse(errorText || '{}');
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
          redirectToLogin();
        }

        errorMessage =
          errorData.message ||
          errorData.error?.message ||
          errorData.error ||
          errorMessage;
      } catch (parseError) {
        // If parsing fails, continue with original error handling
        if (errorText) {
          errorMessage = errorText;
        }
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    customLog('Response data:', JSON.stringify(data, null, 2));

    // Check for session expired in successful response
    if (
      data.status === false &&
      data.message === 'Invalid or expired admin session'
    ) {
      customLog('Session expired in response, triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
      redirectToLogin();
    }

    return data;
  }

  // Promo Code Management APIs
  async getPromoCodes(
    token: string,
    params: PromoCodeQueryParams = {}
  ): Promise<PromoCodeListResponse> {
    customLog('API Service: getPromoCodes called');
    customLog('API Service: Token:', token ? 'Present' : 'Missing');
    customLog('API Service: Params:', params);

    const queryString = new URLSearchParams();

    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.status !== undefined)
      queryString.append('status', params.status.toString());
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);

    const endpoint = `/admin/promo-codes${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    customLog('API Service: Final endpoint:', endpoint);
    customLog(
      'API Service: Authorization header:',
      `Bearer ${token.substring(0, 20)}...`
    );

    const response = await this.request<ApiPromoCodeListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    customLog('=== API Service Response Analysis ===');
    customLog('Full response object:', response);
    customLog('response keys:', Object.keys(response));
    customLog('response.status:', (response as any).status);
    customLog('response.message:', (response as any).message);
    customLog('response.data:', response.data);
    customLog('response.data type:', typeof response.data);

    // Check if response.data exists and has nested data/meta
    if (response.data && typeof response.data === 'object') {
      customLog('response.data keys:', Object.keys(response.data));
      customLog('response.data.data:', (response.data as any).data);
      customLog('response.data.meta:', (response.data as any).meta);
    }

    // Handle both nested and flat response structures
    if (!response.data) {
      customLog('API Service: Warning - response.data is undefined');
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: 10 },
      };
    }

    // Check if response.data has the nested structure or is flat
    const responseData = response.data as any;

    // If response.data is an array (flat structure), use response metadata
    if (Array.isArray(responseData)) {
      const result = {
        data: responseData,
        meta: {
          total: (response as any).total || 0,
          page: (response as any).page || 1,
          lastPage: (response as any).lastPage || 1,
          limit: params.limit || 10,
        },
      };

      customLog('=== Flat Structure Result ===');
      customLog('result.data length:', result.data.length);
      customLog('result.meta:', result.meta);

      return result;
    }

    // Handle nested structure
    const result = {
      data: responseData.data || responseData || [],
      meta: responseData.meta || { total: 0, page: 1, lastPage: 1, limit: 10 },
    };

    customLog('=== Final Parsed Result ===');
    customLog('result.data length:', result.data.length);
    customLog('result.meta:', result.meta);

    return result;
  }

  async getPromoCode(token: string, id: number): Promise<PromoCode> {
    const response = await this.request<ApiPromoCodeResponse>(
      `/admin/promo-codes/${id}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  async createPromoCode(
    token: string,
    promoCodeData: CreatePromoCodeDto
  ): Promise<PromoCode> {
    const response = await this.request<ApiPromoCodeResponse>(
      '/admin/promo-codes',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(promoCodeData),
      }
    );
    return response.data;
  }

  async updatePromoCode(
    token: string,
    id: number,
    promoCodeData: UpdatePromoCodeDto
  ): Promise<PromoCode> {
    const response = await this.request<ApiPromoCodeResponse>(
      `/admin/promo-codes/${id}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(promoCodeData),
      }
    );
    return response.data;
  }

  async deletePromoCode(
    token: string,
    id: number
  ): Promise<{ message: string }> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data?: unknown;
    }>(`/admin/promo-codes/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { message: response.message };
  }
}

// Export a singleton instance
export const promoCodesService = new PromoCodesService();
